/**
 * DRE Dashboard - tRPC Enhanced Version
 *
 * Executive-level income statement dashboard with:
 * - Hero KPIs with sparklines
 * - Period comparison (vs Previous Period / vs Same Period Last Year)
 * - Interactive charts (Evolution, Waterfall)
 * - Collapsible sections in the detailed table
 * - Drill-down links for each line item
 *
 * Styled for Monutrack dark theme with Monumenta Green (#D2FA52) as primary accent.
 */

import { trpc } from '@/api/trpc';
import { useAuth } from '@/hooks/useAuth';
import type { AppRouter, DRELineItem } from '@ds/ds-api-types/types';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import type { inferRouterOutputs } from '@trpc/server';
import { Receipt, TrendingUp, TrendingDown, Eye, EyeOff, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { AnimatedValue, FinancialSkeleton, FinancialEmptyState, InsightsPanel } from '../shared/components';
import { useInsights } from '../shared/hooks';
import { SensitiveValue } from '@/components/privacy/SensitiveText';
import { usePageTitle } from '@/stores/page-title-store';
import type { ClientAggregation } from '../shared/types';
import {
  DREChart,
  DREControlsPanel,
  WaterfallChart,
  calculateComparisonRange,
  calculatePeriodDates,
  SavePresetModal,
  type ComparisonMode,
  type DREChartDataPoint,
  type DREChartType,
  type DREGranularity,
  type PeriodDates,
  type FilterPreset,
} from './components';
import { type ProaComparisonState, getScenarioDisplayName } from './components/ProaComparisonSelector';
import { useFilterPresets } from './hooks/useFilterPresets';
import { useListScenarios, useGetMetricProas, type ProaScenario, type MetricProa, type MetricBreakdown } from '../proa/hooks/useProaData';

// ============================================================================
// TYPES
// ============================================================================

type RelatorioConsolidadoOutput = inferRouterOutputs<AppRouter>['dre']['getReport'][number];

// Collapsed sections state - now includes receita_bruta_breakdown for client revenue drill-down
type SectionId = 'receita' | 'impostos' | 'custos' | 'despesas' | 'resultado' | 'despesas_financeiras' | 'impostos_lucro' | 'receita_bruta_breakdown';

// DRE Line Item structure for display
interface DRERowItem {
  id: string;
  label: string;
  section: SectionId | null;
  category: 'header' | 'revenue' | 'tax' | 'cost' | 'expense' | 'result' | 'total' | 'client_breakdown';
  indent: number;
  getValue: (row: RelatorioConsolidadoOutput) => number | null;
  isTotal?: boolean;
  isBold?: boolean;
  drillDownLineItem?: DRELineItem;
  /** For expandable rows - indicates this row can be expanded */
  isExpandable?: boolean;
  /** For expandable rows - the section ID that gets toggled */
  expandsSection?: SectionId;
  /** Static value for client breakdown rows (doesn't come from RelatorioConsolidadoOutput) */
  staticValue?: number;
  /** Client count for "OUTROS" row */
  clientCount?: number;
  /** Monthly breakdown for client rows - keyed by period "YYYY-MM" */
  monthlyValues?: Record<string, number>;
  /** Raw client name for display (without formatting/indentation) */
  clientName?: string;
  /** Client code for navigation to client-matrix */
  codigoCliente?: string;
  /** Whether this is the "OUTROS" aggregated row */
  isOutros?: boolean;
}

// KPI definitions
interface KPIDefinition {
  key: string;
  label: string;
  icon: string;
  getValue: (totals: Record<string, number>) => number;
  getSubtitle?: (value: number, receitaBruta: number) => string;
  valueColor?: string;
  sparklineColor?: string;
  /** When true, a positive delta means cost increased (bad) — invert the color logic */
  invertDelta?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MONTH_NAMES = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const QUARTER_NAMES = ['Q1', 'Q2', 'Q3', 'Q4'];

// KPI definitions matching chart metrics
const KPI_DEFINITIONS: KPIDefinition[] = [
  { key: 'faturamento', label: 'Faturamento', icon: '', getValue: (t) => t['faturamento'] || 0 },
  { key: 'receitaBruta', label: 'Receita Bruta', icon: '', getValue: (t) => t['receitaBruta'] || 0 },
  { key: 'receitaFinanceira', label: 'Rec. Financeira', icon: '', getValue: (t) => t['receitaFinanceira'] || 0 },
  {
    key: 'lucroLiquido',
    label: 'Lucro Líquido',
    icon: '',
    getValue: (t) => t['lucroLiquido'] || 0,
    getSubtitle: (value, receitaBruta) => receitaBruta ? `${((value / receitaBruta) * 100).toFixed(1)}% da Receita (margem)` : '',
    valueColor: 'text-[#D2FA52]',
    sparklineColor: '#D2FA52',
  },
  { key: 'folha', label: 'Folha', icon: '', getValue: (t) => t['folha'] || 0, valueColor: 'text-red-400', invertDelta: true },
  { key: 'custosExFolha', label: 'Custos Operação', icon: '', getValue: (t) => t['custosExFolha'] || 0, valueColor: 'text-red-400', invertDelta: true },
  { key: 'despesasOperacionais', label: 'Desp. Operac.', icon: '', getValue: (t) => t['despesasOperacionais'] || 0, valueColor: 'text-red-400', invertDelta: true },
  { key: 'impostos', label: 'Impostos', icon: '', getValue: (t) => t['impostos'] || 0, valueColor: 'text-red-400', invertDelta: true },
];

// Helper to safely get numeric value
const getVal = (v: number | null | undefined): number | null => v != null ? v : null;
const getNeg = (v: number | null | undefined): number | null => v != null ? -v : null;

// DRE Structure
const DRE_STRUCTURE: DRERowItem[] = [
  // RECEITA
  { id: 'receita_header', label: '1. RECEITA', section: null, category: 'header', indent: 0, getValue: () => null, isBold: true },
  { id: 'faturamento_bruto', label: 'Faturamento Bruto', section: 'receita', category: 'revenue', indent: 1, getValue: (r) => getVal(r.FaturamentoTotal), drillDownLineItem: 'ReceitaBruta' },
  { id: 'repasse', label: '(-) Repasse / Simples Remessa', section: 'receita', category: 'expense', indent: 1, getValue: (r) => getNeg(r.Repasse), drillDownLineItem: 'Repasse' },
  { id: 'receita_bruta', label: 'Receita Bruta', section: 'receita', category: 'revenue', indent: 1, getValue: (r) => getVal(r.ReceitaBruta), isBold: true, drillDownLineItem: 'ReceitaBruta', isExpandable: true, expandsSection: 'receita_bruta_breakdown' },
  // Client breakdown rows are dynamically inserted here based on getRevenueByClient data
  { id: 'receita_fluxo', label: '(+) Receita Fluxo Gerencial', section: 'receita', category: 'revenue', indent: 1, getValue: (r) => getVal(r.ReceitaFluxoGerencial), drillDownLineItem: 'ReceitaBruta_FluxoGerencial' },

  // IMPOSTOS SOBRE FATURAMENTO
  { id: 'impostos_header', label: '2. IMPOSTOS SOBRE FATURAMENTO', section: null, category: 'header', indent: 0, getValue: () => null, isBold: true },
  { id: 'pis', label: '(-) PIS', section: 'impostos', category: 'tax', indent: 1, getValue: (r) => getNeg(r.PIS), drillDownLineItem: 'PIS' },
  { id: 'cofins', label: '(-) COFINS', section: 'impostos', category: 'tax', indent: 1, getValue: (r) => getNeg(r.COFINS), drillDownLineItem: 'COFINS' },
  { id: 'iss', label: '(-) ISS', section: 'impostos', category: 'tax', indent: 1, getValue: (r) => getNeg(r.ISS), drillDownLineItem: 'ISS' },
  { id: 'receita_liquida', label: 'RECEITA LÍQUIDA', section: null, category: 'total', indent: 0, getValue: (r) => getVal(r.ReceitaLiquida), isTotal: true, isBold: true },

  // CUSTOS
  { id: 'custos_header', label: '3. CUSTOS', section: null, category: 'header', indent: 0, getValue: () => null, isBold: true },
  { id: 'servicos_prestados', label: '(-) Serviços Prestados (Terceiros)', section: 'custos', category: 'cost', indent: 1, getValue: (r) => getNeg(r.ServicosPrestados), drillDownLineItem: 'ServicosPrestados' },
  { id: 'timesheet', label: '(-) Folha Timesheet', section: 'custos', category: 'cost', indent: 1, getValue: (r) => getNeg(r.Timesheet), drillDownLineItem: 'Timesheet' },
  { id: 'folha', label: '(-) Folha Comum', section: 'custos', category: 'cost', indent: 1, getValue: (r) => getNeg(r.Folha), drillDownLineItem: 'Folha' },
  { id: 'mao_de_obra', label: '(-) Mão de Obra', section: 'custos', category: 'cost', indent: 1, getValue: (r) => getNeg(r.MaoDeObra), drillDownLineItem: 'MaoDeObra' },
  { id: 'freelancer', label: '    Freelancer', section: 'custos', category: 'cost', indent: 2, getValue: (r) => getNeg(r.Freelancer), drillDownLineItem: 'Freelancer' },
  { id: 'freelancer_fixo', label: '    Freelancer Fixo', section: 'custos', category: 'cost', indent: 2, getValue: (r) => getNeg(r.FreelancerFixo), drillDownLineItem: 'FreelancerFixo' },
  { id: 'freelancer_empresa', label: '    Freelancer Empresa', section: 'custos', category: 'cost', indent: 2, getValue: (r) => getNeg(r.FreelancerEmpresa), drillDownLineItem: 'FreelancerEmpresa' },
  { id: 'bonificacao', label: '(-) Bonificação', section: 'custos', category: 'cost', indent: 1, getValue: (r) => getNeg(r.Bonificacao), drillDownLineItem: 'Bonificacao' },
  { id: 'lucro_bruto', label: 'LUCRO BRUTO', section: null, category: 'total', indent: 0, getValue: (r) => getVal(r.LucroBruto), isTotal: true, isBold: true },

  // DESPESAS OPERACIONAIS
  { id: 'despesas_header', label: '4. DESPESAS OPERACIONAIS', section: null, category: 'header', indent: 0, getValue: () => null, isBold: true },
  { id: 'despesas_operacionais', label: '(-) Despesas Operacionais', section: 'despesas', category: 'expense', indent: 1, getValue: (r) => getNeg(r.DespesasOperacionais), drillDownLineItem: 'DespesasOperacionais' },

  // RESULTADO OPERACIONAL
  { id: 'resultado_header', label: '5. RESULTADO OPERACIONAL', section: null, category: 'header', indent: 0, getValue: () => null, isBold: true },
  { id: 'receitas_financeiras', label: '(+) Receitas Financeiras', section: 'resultado', category: 'result', indent: 1, getValue: (r) => getVal(r.ReceitasFinanceiras), drillDownLineItem: 'ReceitasFinanceiras' },
  { id: 'despesas_financeiras_grouper', label: '(-) Despesas Financeiras', section: 'resultado', category: 'result', indent: 1, getValue: (r) => getNeg(r.DespesasFinanceiras), isBold: true, isExpandable: true, expandsSection: 'despesas_financeiras' },
  { id: 'despesas_financeiras', label: 'Despesas Financeiras Detalhadas', section: 'despesas_financeiras', category: 'result', indent: 2, getValue: (r) => getNeg(r.DespesasFinanceiras), drillDownLineItem: 'DespesasFinanceiras' },
  { id: 'lucro_operacional', label: 'LUCRO OPERACIONAL', section: null, category: 'total', indent: 0, getValue: (r) => getVal(r.LucroOperacional), isTotal: true, isBold: true },

  // IMPOSTOS SOBRE LUCRO
  { id: 'impostos_lucro_header', label: '6. IMPOSTOS SOBRE LUCRO', section: null, category: 'header', indent: 0, getValue: () => null, isBold: true },
  { id: 'irpj', label: '(-) IRPJ', section: 'impostos_lucro', category: 'tax', indent: 1, getValue: (r) => getNeg(r.IRPJ), drillDownLineItem: 'IRPJ' },
  { id: 'csll', label: '(-) Contribuição Social', section: 'impostos_lucro', category: 'tax', indent: 1, getValue: (r) => getNeg(r.ContribuicaoSocial), drillDownLineItem: 'ContribuicaoSocial' },
  { id: 'lucro_liquido', label: 'LUCRO LÍQUIDO', section: null, category: 'total', indent: 0, getValue: (r) => getVal(r.LucroLiquido), isTotal: true, isBold: true },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function toNum(val: unknown): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (typeof val === 'object' && val !== null && 'toNumber' in val) {
    return (val as { toNumber: () => number }).toNumber();
  }
  const n = Number(val);
  return isNaN(n) ? 0 : n;
}

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  const displayValue = Object.is(value, -0) || value === 0 ? 0 : value;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(displayValue);
}

function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  if (absValue >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}K`;
  return `R$ ${value.toFixed(0)}`;
}

function getDefaultDateRange(): PeriodDates {
  return calculatePeriodDates('ytd');
}

function getQuarter(month: number): number {
  return Math.ceil(month / 3);
}

// ============================================================================
// AGGREGATION HELPERS
// ============================================================================

function aggregateByQuarter(
  monthlyData: Record<string, RelatorioConsolidadoOutput>
): Record<string, RelatorioConsolidadoOutput[]> {
  const quarters: Record<string, RelatorioConsolidadoOutput[]> = {};
  Object.entries(monthlyData).forEach(([monthKey, data]) => {
    const [year, month] = monthKey.split('-').map(Number);
    const quarter = getQuarter(month);
    const quarterKey = `${year}-Q${quarter}`;
    if (!quarters[quarterKey]) quarters[quarterKey] = [];
    quarters[quarterKey].push(data);
  });
  return quarters;
}

function aggregateByYear(
  monthlyData: Record<string, RelatorioConsolidadoOutput>
): Record<string, RelatorioConsolidadoOutput[]> {
  const years: Record<string, RelatorioConsolidadoOutput[]> = {};
  Object.entries(monthlyData).forEach(([monthKey, data]) => {
    const year = monthKey.split('-')[0];
    if (!years[year]) years[year] = [];
    years[year].push(data);
  });
  return years;
}

function sumField(
  rows: RelatorioConsolidadoOutput[],
  getValue: (row: RelatorioConsolidadoOutput) => number | null
): number | null {
  let hasValue = false;
  let total = 0;
  rows.forEach(row => {
    const val = getValue(row);
    if (val !== null) {
      hasValue = true;
      total += toNum(val);
    }
  });
  return hasValue ? total : null;
}

// ============================================================================
// SPARKLINE COMPONENT
// ============================================================================

function formatCompactBRL(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}R$ ${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}R$ ${(abs / 1_000).toFixed(0)}K`;
  return `${sign}R$ ${abs.toFixed(0)}`;
}

interface SparklineProps {
  data: number[];
  labels?: string[];
  width?: number;
  height?: number;
  color?: string;
  showArea?: boolean;
}

function Sparkline({ data, labels, width = 80, height = 24, color = '#D2FA52', showArea = true }: SparklineProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (data.length < 2) return null;

  const pad = 4; // padding to avoid clipping dots
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  // Calculate points with padding offset
  const pointCoords = data.map((value, index) => ({
    x: pad + (index / (data.length - 1)) * innerW,
    y: pad + innerH - ((value - min) / range) * innerH,
  }));

  // Monotone cubic interpolation (smooth, no overshoots)
  const smoothCurve = (pts: { x: number; y: number }[]): string => {
    const n = pts.length;
    if (n < 2) return '';
    if (n === 2) return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;

    const dx: number[] = [];
    const dy: number[] = [];
    const m: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      dx.push(pts[i + 1].x - pts[i].x);
      dy.push(pts[i + 1].y - pts[i].y);
      m.push(dy[i] / dx[i]);
    }
    const tangents: number[] = [m[0]];
    for (let i = 1; i < n - 1; i++) {
      tangents.push(m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2);
    }
    tangents.push(m[n - 2]);

    for (let i = 0; i < n - 1; i++) {
      if (Math.abs(m[i]) < 1e-6) {
        tangents[i] = 0;
        tangents[i + 1] = 0;
      } else {
        const a = tangents[i] / m[i];
        const b = tangents[i + 1] / m[i];
        const s = a * a + b * b;
        if (s > 9) {
          const t = 3 / Math.sqrt(s);
          tangents[i] = t * a * m[i];
          tangents[i + 1] = t * b * m[i];
        }
      }
    }

    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < n - 1; i++) {
      const seg = dx[i] / 3;
      d += ` C ${pts[i].x + seg} ${pts[i].y + tangents[i] * seg}, ${pts[i + 1].x - seg} ${pts[i + 1].y - tangents[i + 1] * seg}, ${pts[i + 1].x} ${pts[i + 1].y}`;
    }
    return d;
  };

  const curvePath = smoothCurve(pointCoords);

  // Create area path (curve + fill to bottom)
  const curvePartForArea = smoothCurve(pointCoords).replace(/^M /, 'L ');
  const areaPath = `M ${pointCoords[0].x} ${height - pad} ${curvePartForArea} L ${pointCoords[pointCoords.length - 1].x} ${height - pad} Z`;

  // Determine trend color: brand green if last > first, red otherwise
  const trend = data[data.length - 1] >= data[0] ? '#D2FA52' : '#f87171';
  const finalColor = color === 'auto' ? trend : color;

  // Unique gradient ID per instance
  const gradientId = `sparkline-grad-${finalColor.replace('#', '')}`;

  // Calculate tooltip position as percentage for HTML overlay
  const getTooltipPos = (idx: number) => {
    const p = pointCoords[idx];
    return {
      left: `${(p.x / width) * 100}%`,
      bottom: `${((height - p.y) / height) * 100 + 12}%`,
    };
  };

  return (
    <div className="relative w-full" style={{ aspectRatio: `${width} / ${height}` }}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full block">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={finalColor} stopOpacity={0.3} />
            <stop offset="100%" stopColor={finalColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        {showArea && (
          <path
            d={areaPath}
            fill={`url(#${gradientId})`}
          />
        )}
        <path
          d={curvePath}
          fill="none"
          stroke={finalColor}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Dots at each data point */}
        {pointCoords.map((p, index) => (
          <circle
            key={index}
            cx={p.x}
            cy={p.y}
            r={hoveredIdx === index ? 3.5 : 2}
            fill={finalColor}
            stroke={hoveredIdx === index ? '#24262B' : 'none'}
            strokeWidth={hoveredIdx === index ? 1.5 : 0}
          />
        ))}
        {/* Invisible wider hit targets for easier hover */}
        {pointCoords.map((p, index) => (
          <circle
            key={`hit-${index}`}
            cx={p.x}
            cy={p.y}
            r={8}
            fill="transparent"
            onMouseEnter={() => setHoveredIdx(index)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </svg>
      {/* HTML Tooltip - positioned outside SVG so it won't clip */}
      {hoveredIdx !== null && labels?.[hoveredIdx] && (
        <div
          className="absolute z-50 pointer-events-none bg-[#1a1c20] border border-[#3A3D44] rounded px-1.5 py-0.5 text-[10px] text-[#e5e7eb] font-medium whitespace-nowrap -translate-x-1/2"
          style={getTooltipPos(hoveredIdx)}
        >
          {labels[hoveredIdx]}: {formatCompactBRL(data[hoveredIdx])}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// KPI CARD COMPONENT
// ============================================================================

interface KPICardProps {
  label: string;
  value: string;
  subtitle?: string;
  change?: number | null;
  icon: string;
  active?: boolean;
  onClick?: () => void;
  sparklineData?: number[];
  sparklineLabels?: string[];
  /** Optional numeric value for animation */
  numericValue?: number;
  /** Format for animated value: 'currency' | 'number' | 'percent' | 'compact-currency' */
  animationFormat?: 'currency' | 'number' | 'percent' | 'compact-currency';
  /** Color for the value text (tailwind class) */
  valueColor?: string;
  /** Hex color for sparkline override */
  sparklineColor?: string;
  /** When true, positive delta = bad (cost increased), negative delta = good (cost decreased) */
  invertDelta?: boolean;
  /** Label for comparison context */
  comparisonLabel?: string;
}

function KPICard({ label, value, subtitle, change, icon, active, onClick, sparklineData, sparklineLabels, numericValue, animationFormat, valueColor, sparklineColor, invertDelta, comparisonLabel }: KPICardProps) {
  const hasChange = change !== null && change !== undefined && !isNaN(change) && Math.abs(change) >= 0.1;
  // For costs/expenses, increasing is bad (red) and decreasing is good (green)
  const isPositive = hasChange && (invertDelta ? change < 0 : change > 0);
  const isNegative = hasChange && (invertDelta ? change > 0 : change < 0);

  const isNeutralChange = hasChange && Math.abs(change) <= 5;
  const sparklineColorResolved = hasChange ? (isNeutralChange ? '#ffffff' : isPositive ? '#D2FA52' : isNegative ? '#f87171' : '#838998') : 'auto';

  return (
    <div
      onClick={onClick}
      className={`
        relative flex flex-col p-4 rounded-xl transition-all duration-150
        bg-[#24262B] border border-[#3A3D44]/50 hover:bg-[#2A2D34]
        ${!active ? 'opacity-20' : ''}
        ${onClick ? 'cursor-pointer' : ''}
      `}
      data-testid="kpi-card"
    >
      {onClick && (
        <div className="absolute top-3 right-3 text-[#838998]">
          {active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        </div>
      )}
      <span className="text-xs text-[#838998] uppercase tracking-wide font-medium mb-2">
        {label}
      </span>
      <span className={`text-2xl font-bold ${valueColor || 'text-white'}`}>
        {numericValue !== undefined && animationFormat ? (
          <AnimatedValue
            value={numericValue}
            format={animationFormat}
            className="animated-value"
          />
        ) : (
          value
        )}
      </span>
      {subtitle && (
        <span className="text-xs text-[#838998] mt-1">{subtitle}</span>
      )}
      {sparklineData && sparklineData.length >= 2 && (
        <div className="mt-3">
          <Sparkline data={sparklineData} labels={sparklineLabels} color={sparklineColorResolved} width={200} height={40} />
        </div>
      )}
      {hasChange && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${isNeutralChange ? 'text-white' : isPositive ? 'text-[#D2FA52]' : isNegative ? 'text-red-400' : 'text-[#838998]'}`}>
          {change > 0 ? <TrendingUp className="h-3 w-3" /> : change < 0 ? <TrendingDown className="h-3 w-3" /> : null}
          <span className="font-medium">{Math.abs(change).toFixed(1)}%</span>
          <span className="text-[#838998]">{comparisonLabel || 'vs período anterior'}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TABLE SKELETON COMPONENT
// ============================================================================

function TableSkeleton({ rows = 10, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="animate-pulse">
      <div className="bg-[#24262B] rounded-xl overflow-hidden">
        <div className="flex border-b border-[#3A3D44] p-4">
          {Array.from({ length: columns }).map((_, i) => (
            <div key={i} className="flex-1 h-4 bg-[#3A3D44] rounded mr-4" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex border-b border-[#3A3D44]/50 p-3">
            {Array.from({ length: columns }).map((_, j) => (
              <div key={j} className="flex-1 h-3 bg-[#2A2D34] rounded mr-4" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// Search params interface for type safety
interface DRESearchParams {
  startDate?: string;
  endDate?: string;
  granularity?: DREGranularity;
  comparison?: ComparisonMode;
}

export function DREDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('DRE');
  const defaultDates = getDefaultDateRange();

  // Get URL search params - with type-safe fallback
  const searchParams = useSearch({ strict: false }) as DRESearchParams;

  // State - initialized from URL params if present, otherwise defaults
  const [startDate, setStartDate] = useState(searchParams.startDate || defaultDates.startDate);
  const [endDate, setEndDate] = useState(searchParams.endDate || defaultDates.endDate);
  const [granularity, setGranularity] = useState<DREGranularity>(searchParams.granularity || 'monthly');
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(searchParams.comparison || 'same_period_last_year');
  const [proaComparison, setProaComparison] = useState<ProaComparisonState>({
    enabled: false,
    selectedScenarioIds: [],
  });

  // Handle PROA comparison change - force accumulated granularity when enabled
  const handleProaComparisonChange = useCallback((newState: ProaComparisonState) => {
    setProaComparison(newState);
    if (newState.enabled) {
      setGranularity('accumulated');
    }
  }, []);
  const [collapsedSections, setCollapsedSections] = useState<Set<SectionId>>(new Set());
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(() =>
    new Set(KPI_DEFINITIONS.map(k => k.key))
  );
  const [activeChart, setActiveChart] = useState<'evolution' | 'waterfall'>('evolution');
  const [evolutionChartType, setEvolutionChartType] = useState<DREChartType>('combo');

  // Filter Presets Hook
  const {
    presets,
    isModalOpen: isPresetModalOpen,
    openModal: openPresetModal,
    closeModal: closePresetModal,
    savePreset: saveFilterPreset,
    deletePreset: deleteFilterPreset,
    checkNameExists,
  } = useFilterPresets();

  // Handle preset save
  const handleSavePreset = useCallback((name: string) => {
    saveFilterPreset(name, {
      startDate,
      endDate,
      granularity,
      comparisonMode,
    });
  }, [startDate, endDate, granularity, comparisonMode, saveFilterPreset]);

  // Handle preset apply
  const handleApplyPreset = useCallback((preset: FilterPreset) => {
    setStartDate(preset.filters.startDate);
    setEndDate(preset.filters.endDate);
    setGranularity(preset.filters.granularity);
    setComparisonMode(preset.filters.comparisonMode);
  }, []);

  // Toggle metric visibility
  const toggleMetric = useCallback((metricKey: string) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev);
      if (next.has(metricKey)) {
        next.delete(metricKey);
      } else {
        next.add(metricKey);
      }
      return next;
    });
  }, []);

  // Calculate comparison date range
  const comparisonRange = useMemo(
    () => calculateComparisonRange(startDate, endDate, comparisonMode),
    [startDate, endDate, comparisonMode]
  );
  // Fetch current period data
  const {
    data: dreData = [],
    isLoading,
    error,
    refetch,
  } = trpc.dre.getReport.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
    }
  );

  // Fetch comparison period data
  const { data: comparisonData = [] } = trpc.dre.getReport.useQuery(
    {
      dataInicial: comparisonRange ? new Date(comparisonRange.startDate) : new Date(),
      dataFinal: comparisonRange ? new Date(comparisonRange.endDate) : new Date(),
    },
    {
      enabled: !!comparisonRange,
      retry: false,
    }
  );

  // PROA year derived from start date (parse directly to avoid timezone issues)
  const proaYear = parseInt(startDate.substring(0, 4), 10);

  // Fetch PROA scenarios for the year
  const { data: proaScenarios = [] } = useListScenarios(proaYear);

  // Get selected scenarios data
  const selectedProaScenarios = useMemo(() => {
    if (!proaComparison.enabled) return [];
    return proaComparison.selectedScenarioIds
      .map((id) => proaScenarios.find((s) => s.scenarioId === id))
      .filter((s): s is ProaScenario => s !== undefined);
  }, [proaComparison.enabled, proaComparison.selectedScenarioIds, proaScenarios]);

  // Fetch PROA metrics for each selected scenario (up to 3 scenarios)
  // We call all 3 hooks unconditionally to follow React rules, but only enable them when needed
  const scenarioId0 = proaComparison.selectedScenarioIds[0] ?? null;
  const scenarioId1 = proaComparison.selectedScenarioIds[1] ?? null;
  const scenarioId2 = proaComparison.selectedScenarioIds[2] ?? null;

  const { data: metrics0 = [] } = useGetMetricProas(
    proaComparison.enabled ? scenarioId0 : null,
    proaYear
  );
  const { data: metrics1 = [] } = useGetMetricProas(
    proaComparison.enabled ? scenarioId1 : null,
    proaYear
  );
  const { data: metrics2 = [] } = useGetMetricProas(
    proaComparison.enabled ? scenarioId2 : null,
    proaYear
  );

  // Build a map of scenarioId -> Map<lineItem, annual value> for quick lookup
  const proaValuesByScenario = useMemo(() => {
    const result = new Map<string, Map<string, number>>();

    if (scenarioId0 && metrics0.length > 0) {
      const map = new Map<string, number>();
      metrics0.forEach((proa: MetricProa) => map.set(proa.lineItem, proa.annual));
      result.set(scenarioId0, map);
    }

    if (scenarioId1 && metrics1.length > 0) {
      const map = new Map<string, number>();
      metrics1.forEach((proa: MetricProa) => map.set(proa.lineItem, proa.annual));
      result.set(scenarioId1, map);
    }

    if (scenarioId2 && metrics2.length > 0) {
      const map = new Map<string, number>();
      metrics2.forEach((proa: MetricProa) => map.set(proa.lineItem, proa.annual));
      result.set(scenarioId2, map);
    }

    return result;
  }, [scenarioId0, scenarioId1, scenarioId2, metrics0, metrics1, metrics2]);

  // Build a map of scenarioId -> Map<lineItem, breakdowns[]> for client-level PROA targets
  const proaBreakdownsByScenario = useMemo(() => {
    const result = new Map<string, Map<string, MetricBreakdown[]>>();

    const buildBreakdownMap = (scenarioId: string, metrics: MetricProa[]) => {
      const map = new Map<string, MetricBreakdown[]>();
      metrics.forEach((proa: MetricProa) => {
        if (proa.breakdowns && proa.breakdowns.length > 0) {
          map.set(proa.lineItem, proa.breakdowns);
        }
      });
      result.set(scenarioId, map);
    };

    if (scenarioId0 && metrics0.length > 0) {
      buildBreakdownMap(scenarioId0, metrics0);
    }
    if (scenarioId1 && metrics1.length > 0) {
      buildBreakdownMap(scenarioId1, metrics1);
    }
    if (scenarioId2 && metrics2.length > 0) {
      buildBreakdownMap(scenarioId2, metrics2);
    }

    return result;
  }, [scenarioId0, scenarioId1, scenarioId2, metrics0, metrics1, metrics2]);

  // Fetch client revenue breakdown for Receita Bruta drill-down
  const { data: clientRevenueData } = trpc.dre.getRevenueByClient.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
    }
  );

  // Process monthly data
  const monthlyData = useMemo(() => {
    const byMonth: Record<string, RelatorioConsolidadoOutput> = {};
    dreData.forEach((row) => {
      const key = `${row.Ano}-${String(row.Mes).padStart(2, '0')}`;
      byMonth[key] = row;
    });
    return byMonth;
  }, [dreData]);

  const comparisonMonthlyData = useMemo(() => {
    const byMonth: Record<string, RelatorioConsolidadoOutput> = {};
    comparisonData.forEach((row) => {
      const key = `${row.Ano}-${String(row.Mes).padStart(2, '0')}`;
      byMonth[key] = row;
    });
    return byMonth;
  }, [comparisonData]);

  const sortedMonths = useMemo(() => Object.keys(monthlyData).sort(), [monthlyData]);
  const quarterlyData = useMemo(() => aggregateByQuarter(monthlyData), [monthlyData]);
  const yearlyData = useMemo(() => aggregateByYear(monthlyData), [monthlyData]);
  const sortedQuarters = useMemo(() => Object.keys(quarterlyData).sort(), [quarterlyData]);
  const sortedYears = useMemo(() => Object.keys(yearlyData).sort(), [yearlyData]);

  // Calculate totals
  const rowTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    DRE_STRUCTURE.forEach((item) => {
      if (item.category !== 'header') {
        let total = 0;
        sortedMonths.forEach((monthKey) => {
          const row = monthlyData[monthKey];
          if (row) {
            const value = item.getValue(row);
            if (value !== null) total += toNum(value);
          }
        });
        totals[item.id] = total;
      }
    });
    return totals;
  }, [monthlyData, sortedMonths]);

  // Comparison totals
  const comparisonTotals = useMemo(() => {
    if (!comparisonRange) return {};
    const totals: Record<string, number> = {};
    const sortedCompMonths = Object.keys(comparisonMonthlyData).sort();
    DRE_STRUCTURE.forEach((item) => {
      if (item.category !== 'header') {
        let total = 0;
        sortedCompMonths.forEach((monthKey) => {
          const row = comparisonMonthlyData[monthKey];
          if (row) {
            const value = item.getValue(row);
            if (value !== null) total += toNum(value);
          }
        });
        totals[item.id] = total;
      }
    });
    return totals;
  }, [comparisonMonthlyData, comparisonRange]);

  // KPI values - use Receita Bruta as base for percentages
  const receitaBrutaTotal = rowTotals['receita_bruta'] || 0;

  // Comparison deltas
  const calcDelta = useCallback((current: number, comparison: number): number | null => {
    if (!comparisonRange || comparison === 0) return null;
    return ((current - comparison) / Math.abs(comparison)) * 100;
  }, [comparisonRange]);

  // Helper to compute chart data point from raw row
  const computeChartDataPoint = useCallback((row: RelatorioConsolidadoOutput | undefined, label: string, period: string): DREChartDataPoint => {
    return {
      period,
      label,
      faturamento: toNum(row?.FaturamentoTotal),
      receitaBruta: toNum(row?.ReceitaBruta),
      receitaFinanceira: toNum(row?.ReceitasFinanceiras),
      impostos: Math.abs(toNum(row?.PIS)) + Math.abs(toNum(row?.COFINS)) + Math.abs(toNum(row?.ISS)) + Math.abs(toNum(row?.IRPJ)) + Math.abs(toNum(row?.ContribuicaoSocial)),
      folha: Math.abs(toNum(row?.Folha)) + Math.abs(toNum(row?.MaoDeObra)),
      custosExFolha: Math.abs(toNum(row?.ServicosPrestados)) + Math.abs(toNum(row?.Timesheet)) + Math.abs(toNum(row?.Bonificacao)),
      despesasOperacionais: Math.abs(toNum(row?.DespesasOperacionais)),
      lucroLiquido: toNum(row?.LucroLiquido),
    };
  }, []);

  // Chart data
  const chartData: DREChartDataPoint[] = useMemo(() => {
    return sortedMonths.map(monthKey => {
      const [year, month] = monthKey.split('-').map(Number);
      const row = monthlyData[monthKey];
      const label = `${MONTH_NAMES[month - 1]}/${String(year).slice(2)}`;
      return computeChartDataPoint(row, label, monthKey);
    });
  }, [sortedMonths, monthlyData, computeChartDataPoint]);

  // Comparison chart data
  const comparisonChartData: DREChartDataPoint[] | undefined = useMemo(() => {
    if (!comparisonRange) return undefined;
    const sortedCompMonths = Object.keys(comparisonMonthlyData).sort();
    return sortedCompMonths.map(monthKey => {
      const [year, month] = monthKey.split('-').map(Number);
      const row = comparisonMonthlyData[monthKey];
      const label = `${MONTH_NAMES[month - 1]}/${String(year).slice(2)}`;
      return computeChartDataPoint(row, label, monthKey);
    });
  }, [comparisonMonthlyData, comparisonRange, computeChartDataPoint]);

  // Helper to compute metric values from rowTotals
  const computeMetricValues = useCallback((totals: Record<string, number>) => {
    return {
      faturamento: totals['faturamento_bruto'] || 0,
      receitaBruta: totals['receita_bruta'] || 0,
      receitaFinanceira: totals['receitas_financeiras'] || 0,
      impostos: Math.abs(totals['pis'] || 0) + Math.abs(totals['cofins'] || 0) + Math.abs(totals['iss'] || 0) + Math.abs(totals['irpj'] || 0) + Math.abs(totals['csll'] || 0),
      folha: Math.abs(totals['folha'] || 0) + Math.abs(totals['mao_de_obra'] || 0),
      custosExFolha: Math.abs(totals['servicos_prestados'] || 0) + Math.abs(totals['timesheet'] || 0) + Math.abs(totals['bonificacao'] || 0),
      despesasOperacionais: Math.abs(totals['despesas_operacionais'] || 0),
      lucroLiquido: totals['lucro_liquido'] || 0,
    };
  }, []);

  // Waterfall values
  const waterfallValues: Record<string, number> = useMemo(() => {
    return computeMetricValues(rowTotals);
  }, [rowTotals, computeMetricValues]);

  // Comparison waterfall values
  const comparisonWaterfallValues: Record<string, number> = useMemo(() => {
    if (!comparisonRange) return {};
    return computeMetricValues(comparisonTotals);
  }, [comparisonTotals, comparisonRange, computeMetricValues]);

  // Sparkline data for each KPI - extracted from chartData
  const sparklineDataByKPI = useMemo((): Record<string, number[]> => {
    const result: Record<string, number[]> = {
      faturamento: [],
      receitaBruta: [],
      receitaFinanceira: [],
      impostos: [],
      folha: [],
      custosExFolha: [],
      despesasOperacionais: [],
      lucroLiquido: [],
    };

    chartData.forEach((dataPoint) => {
      result.faturamento.push(dataPoint.faturamento);
      result.receitaBruta.push(dataPoint.receitaBruta);
      result.receitaFinanceira.push(dataPoint.receitaFinanceira);
      result.impostos.push(dataPoint.impostos);
      result.folha.push(dataPoint.folha);
      result.custosExFolha.push(dataPoint.custosExFolha);
      result.despesasOperacionais.push(dataPoint.despesasOperacionais);
      result.lucroLiquido.push(dataPoint.lucroLiquido);
    });

    return result;
  }, [chartData]);

  // Labels for sparkline tooltips (month names from chartData)
  const sparklineLabels = useMemo(() => chartData.map(d => d.label), [chartData]);

  // Generate insights for the InsightsPanel
  // For DRE, we use receitaBruta as the main metric and compare with same period last year
  const comparisonReceitaBruta = comparisonTotals['receita_bruta'] || 0;

  // Build client aggregations from DRE data if available
  // Note: DRE doesn't have per-client data, so we pass empty array
  // Client concentration insights will only show on Client Matrix page
  const dreClientAggregations: ClientAggregation[] = [];

  const { insights: dreInsights } = useInsights({
    currentPeriodTotal: receitaBrutaTotal,
    samePeriodLastYear: comparisonMode === 'same_period_last_year' ? comparisonReceitaBruta : undefined,
    clientAggregations: dreClientAggregations,
    context: 'dre',
  });

  // Toggle section collapse
  const toggleSection = useCallback((sectionId: SectionId) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Handle drill-down navigation to /financial/dre/drilldown/:lineItem
  const handleDrillDown = useCallback((lineItem: DRELineItem, drillStartDate: string, drillEndDate: string) => {
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate: drillStartDate,
        endDate: drillEndDate,
        granularity,
      },
    });
  }, [navigate, granularity]);

  // Handle client drill-down navigation to /financial/client-matrix/:clientId
  const handleClientDrillDown = useCallback((codigoCliente: string) => {
    void navigate({
      to: '/financial/client-matrix/$clientId',
      params: { clientId: codigoCliente },
      search: {
        startDate,
        endDate,
        ...(comparisonRange ? {
          compareStartDate: comparisonRange.startDate,
          compareEndDate: comparisonRange.endDate,
        } : {}),
        granularity,
      },
    });
  }, [navigate, startDate, endDate, comparisonRange, granularity]);

  // Build dynamic rows including client breakdown
  // This injects client revenue rows after "Receita Bruta" when expanded
  // Note: PROA targets are looked up at render time in columns, not stored here
  const dynamicDREStructure = useMemo((): DRERowItem[] => {
    const result: DRERowItem[] = [];

    for (const row of DRE_STRUCTURE) {
      result.push(row);

      // After "Receita Bruta", inject client breakdown rows if data is available
      if (row.id === 'receita_bruta' && clientRevenueData?.clients) {
        for (const client of clientRevenueData.clients) {
          // Convert monthly array to Record<period, receita> for easy lookup
          const monthlyValues: Record<string, number> = {};
          if (client.monthly) {
            for (const m of client.monthly) {
              monthlyValues[m.period] = m.receita;
            }
          }

          result.push({
            id: client.id,
            label: client.isOutros
              ? `    OUTROS (${client.clientCount ?? 0} clientes)`
              : `    ${client.nomeCliente}`,
            section: 'receita_bruta_breakdown',
            category: 'client_breakdown',
            indent: 2,
            getValue: () => null, // Not used - we use staticValue/monthlyValues instead
            staticValue: client.receita,
            clientCount: client.clientCount,
            monthlyValues,
            clientName: client.nomeCliente,
            codigoCliente: client.codigoCliente,
            isOutros: client.isOutros,
          });
        }
      }
    }

    return result;
  }, [clientRevenueData]);

  // Filter visible rows based on collapsed sections
  const visibleRows = useMemo(() => {
    return dynamicDREStructure.filter(row => {
      if (row.section && collapsedSections.has(row.section)) {
        return false;
      }
      // Hide child sections when parent is collapsed
      if (row.section === 'despesas_financeiras' && collapsedSections.has('resultado')) {
        return false;
      }
      if (row.section === 'receita_bruta_breakdown' && collapsedSections.has('receita')) {
        return false;
      }
      return true;
    });
  }, [dynamicDREStructure, collapsedSections]);

  // Column helper
  const columnHelper = createColumnHelper<DRERowItem>();

  // Build columns
  const columns = useMemo((): ColumnDef<DRERowItem, unknown>[] => {
    const cols: ColumnDef<DRERowItem, unknown>[] = [
      columnHelper.accessor('label', {
        header: 'Detalhamento',
        cell: (info) => {
          const item = info.row.original;
          const isHeader = item.category === 'header';
          const isClientBreakdown = item.category === 'client_breakdown';
          const isExpandableRow = item.isExpandable && item.expandsSection;

          // Determine section for header rows
          const sectionId = isHeader ? (
            item.id === 'receita_header' ? 'receita' :
            item.id === 'impostos_header' ? 'impostos' :
            item.id === 'custos_header' ? 'custos' :
            item.id === 'despesas_header' ? 'despesas' :
            item.id === 'resultado_header' ? 'resultado' :
            item.id === 'impostos_lucro_header' ? 'impostos_lucro' : null
          ) : null;

          // Determine if this row is expandable (Receita Bruta)
          const expandableSection = isExpandableRow ? item.expandsSection : null;

          return (
            <div
              style={{ paddingLeft: `${item.indent * 20}px` }}
              className={`
                flex items-center gap-2
                ${item.isBold ? 'font-bold' : 'font-normal'}
                ${item.category === 'header' ? 'text-white' : ''}
                ${item.id === 'lucro_liquido' ? 'text-brand-dark-deepest text-base tracking-wide' : item.isTotal ? 'text-white text-base tracking-wide' : ''}
                ${isClientBreakdown ? 'text-[#838998] text-sm' : ''}
                ${!isHeader && !item.isTotal && !isClientBreakdown ? 'text-white' : ''}
                ${(isHeader && sectionId) || isExpandableRow ? 'cursor-pointer' : ''}
              `}
              onClick={() => {
                if (sectionId) toggleSection(sectionId);
                if (expandableSection) toggleSection(expandableSection);
              }}
            >
              {/* Show chevron for header sections */}
              {isHeader && sectionId && (
                <ChevronRight
                  size={14}
                  className={`text-[#838998] transition-transform duration-200 ${
                    collapsedSections.has(sectionId) ? '' : 'rotate-90'
                  }`}
                />
              )}
              {/* Show chevron for expandable rows like Receita Bruta */}
              {isExpandableRow && expandableSection && (
                <ChevronRight
                  size={14}
                  className={`text-[#838998] transition-transform duration-200 ${
                    collapsedSections.has(expandableSection) ? '' : 'rotate-90'
                  }`}
                />
              )}
              {item.label}
            </div>
          );
        },
      }) as ColumnDef<DRERowItem, unknown>,
    ];

    // Helper to create value cell
    // For client breakdown rows, pass period (e.g., "2024-01") to get monthly value
    const createValueCell = (
      item: DRERowItem,
      value: number | null,
      drillStartDate: string,
      drillEndDate: string,
      title: string,
      period?: string // Optional period for client breakdown monthly values
    ) => {
      if (item.category === 'header') return null;

      // Handle client breakdown rows with monthly or static values
      if (item.category === 'client_breakdown') {
        // If period is provided and we have monthly values, use the monthly value
        // Otherwise fall back to staticValue (for TOTAL columns)
        const clientValue = period && item.monthlyValues
          ? (item.monthlyValues[period] ?? 0)
          : (item.staticValue ?? 0);

        const clientColor = clientValue === 0 ? 'text-brand-gray-dark' : 'text-[#838998]';

        // Make individual clients clickable (not OUTROS) - requires codigoCliente for navigation
        if (!item.isOutros && item.codigoCliente) {
          return (
            <div
              onClick={() => handleClientDrillDown(item.codigoCliente!)}
              className={`text-right text-sm no-underline cursor-pointer hover:underline hover:text-white ${clientColor}`}
              title={`Ver detalhes de ${item.clientName}`}
            >
              <SensitiveValue mask="R$ ••••••">{formatCurrency(clientValue)}</SensitiveValue>
            </div>
          );
        }

        return (
          <div className={`text-right text-sm ${clientValue === 0 ? 'text-brand-gray-dark' : 'text-[#838998]'}`}>
            <SensitiveValue mask="R$ ••••••">{formatCurrency(clientValue)}</SensitiveValue>
          </div>
        );
      }

      // Determine value color based on category
      const isZero = value === null || value === 0;
      const getValueColor = () => {
        if (isZero) return 'text-brand-gray-dark';
        if (item.id === 'lucro_liquido') return value !== null && value < 0 ? 'text-red-400' : 'text-brand-dark-deepest';
        if (item.category === 'tax' || item.category === 'cost' || item.category === 'expense') return 'text-red-400';
        if (value !== null && value < 0) return 'text-red-400';
        if (item.isTotal) return 'text-white';
        if (item.category === 'revenue') return 'text-white';
        return 'text-white';
      };

      if (item.drillDownLineItem && value !== null) {
        return (
          <div
            onClick={() => handleDrillDown(item.drillDownLineItem!, drillStartDate, drillEndDate)}
            className={`text-right font-medium no-underline cursor-pointer ${getValueColor()} hover:underline hover:brightness-125`}
            title={title}
          >
            <SensitiveValue mask="R$ ••••••">{formatCurrency(value)}</SensitiveValue>
          </div>
        );
      }

      return (
        <div className={`
          text-right
          ${item.isBold ? 'font-bold' : 'font-normal'}
          ${getValueColor()}
        `}>
          <SensitiveValue mask="R$ ••••••">{formatCurrency(value)}</SensitiveValue>
        </div>
      );
    };

    // ACCUMULATED VIEW
    if (granularity === 'accumulated') {
      cols.push(
        columnHelper.display({
          id: 'accumulated_total',
          header: () => <div className="text-right font-bold text-white">TOTAL ACUMULADO</div>,
          cell: (info) => {
            const item = info.row.original;
            if (item.category === 'header') return null;
            const total = rowTotals[item.id] ?? 0;
            return createValueCell(item, total, startDate, endDate, `Ver detalhamento de ${item.label}`);
          },
        })
      );

      // Comparison column
      if (comparisonRange) {
        cols.push(
          columnHelper.display({
            id: 'comparison_total',
            header: () => <div className="text-right font-bold text-[#838998]">COMPARAÇÃO</div>,
            cell: (info) => {
              const item = info.row.original;
              if (item.category === 'header') return null;
              const total = comparisonTotals[item.id] ?? 0;
              return (
                <div className="text-right text-[#838998]">
                  <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                </div>
              );
            },
          }),
          columnHelper.display({
            id: 'delta',
            header: () => <div className="text-right font-bold text-white">Δ %</div>,
            cell: (info) => {
              const item = info.row.original;
              if (item.category === 'header') return null;
              const current = rowTotals[item.id] ?? 0;
              const comparison = comparisonTotals[item.id] ?? 0;
              const delta = calcDelta(current, comparison);
              if (delta === null) return <div className="text-right text-[#838998]">-</div>;
              const isPositive = delta > 0;
              const isNegative = delta < 0;
              return (
                <div className={`
                  text-right font-semibold
                  ${isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-[#838998]'}
                `}>
                  {isPositive ? '▲' : isNegative ? '▼' : ''}{isPositive ? '+' : ''}{delta.toFixed(1)}%
                </div>
              );
            },
          })
        );
      }

      // Margin column
      cols.push(
        columnHelper.display({
          id: 'margin',
          header: () => <div className="text-right font-bold text-[#838998]">% Rec.</div>,
          cell: (info) => {
            const item = info.row.original;
            if (item.category === 'header') return null;
            // For client breakdown rows, use staticValue and show % of Receita Bruta
            const value = item.category === 'client_breakdown'
              ? (item.staticValue ?? 0)
              : (rowTotals[item.id] ?? 0);
            const margin = receitaBrutaTotal ? (value / receitaBrutaTotal) * 100 : 0;
            return (
              <div className="text-right text-[#838998] text-sm">
                {margin.toFixed(1)}%
              </div>
            );
          },
        })
      );

      // PROA columns (when enabled) - Accumulated view
      // Each PROA gets 3 columns with a grouped header showing scenario name
      if (proaComparison.enabled && selectedProaScenarios.length > 0) {
        const isMultipleProas = selectedProaScenarios.length > 1;

        selectedProaScenarios.forEach((scenario, scenarioIndex) => {
          const scenarioName = getScenarioDisplayName(scenario);
          const isOfficial = scenario.isOfficial;

          // Use different accent colors for different scenarios
          const accentColors = [
            { bg: 'bg-[#D2FA52]/10', border: 'border-l-[#D2FA52]', text: 'text-[#D2FA52]' },
            { bg: 'bg-[#52D2FA]/10', border: 'border-l-[#52D2FA]', text: 'text-[#52D2FA]' },
            { bg: 'bg-[#FA52D2]/10', border: 'border-l-[#FA52D2]', text: 'text-[#FA52D2]' },
          ];
          const colors = accentColors[scenarioIndex % accentColors.length];

          // Get the metrics map for this specific scenario
          const scenarioMetrics = proaValuesByScenario.get(scenario.scenarioId);

          // Helper to determine if a row represents an expense (where lower actual is good)
          // Expenses are shown as negative in DRE (using getNeg) but PROA stores them positive
          // We detect expenses by checking if the actual value is negative
          const isExpenseRow = (item: DRERowItem) => {
            const actual = rowTotals[item.id] ?? 0;
            return actual < 0;
          };

          // Get breakdowns for this scenario to match client-level PROA targets
          const scenarioBreakdowns = proaBreakdownsByScenario.get(scenario.scenarioId);

          // PROA Value column (with scenario name in header)
          cols.push(
            columnHelper.display({
              id: `proa_${scenario.scenarioId}`,
              header: () => (
                <div className={`text-right ${isMultipleProas ? 'border-l-2 pl-2 ' + colors.border : ''}`}>
                  <div className={`font-bold ${colors.text} text-xs`}>
                    {isOfficial ? '' : ''}{scenarioName}
                  </div>
                  <div className="text-[#838998] text-xs font-normal">Meta</div>
                </div>
              ),
              cell: (info) => {
                const item = info.row.original;
                if (item.category === 'header') return null;

                // Handle client breakdown rows - look up PROA target by client code
                if (item.category === 'client_breakdown') {
                  if (item.isOutros || !item.codigoCliente) {
                    return <div className={`text-right text-[#5A5D66] text-sm ${isMultipleProas ? 'border-l-2 pl-2 ' + colors.border : ''}`}>-</div>;
                  }

                  // Find breakdown for this client in ReceitaBruta line
                  const receitaBreakdowns = scenarioBreakdowns?.get('ReceitaBruta') ?? [];
                  const clientBreakdown = receitaBreakdowns.find(b => b.dimensionId === item.codigoCliente);
                  const clientProaValue = clientBreakdown?.annual ?? null;

                  if (clientProaValue === null) {
                    return <div className={`text-right text-[#5A5D66] text-sm ${isMultipleProas ? 'border-l-2 pl-2 ' + colors.border : ''}`}>-</div>;
                  }

                  return (
                    <div className={`text-right ${colors.text} text-sm ${isMultipleProas ? 'border-l-2 pl-2 ' + colors.border : ''}`}>
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(clientProaValue)}</SensitiveValue>
                    </div>
                  );
                }

                const proaValue = item.drillDownLineItem && scenarioMetrics
                  ? scenarioMetrics.get(item.drillDownLineItem) ?? null
                  : null;

                if (proaValue === null) {
                  return <div className={`text-right text-[#5A5D66] ${isMultipleProas ? 'border-l-2 pl-2 ' + colors.border : ''}`}>-</div>;
                }

                return (
                  <div className={`text-right ${colors.text} ${colors.bg} p-1 rounded ${isMultipleProas ? 'border-l-2 ' + colors.border : ''}`}>
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(proaValue)}</SensitiveValue>
                  </div>
                );
              },
            })
          );

          // PROA Diferença column
          cols.push(
            columnHelper.display({
              id: `proa_diff_${scenario.scenarioId}`,
              header: () => (
                <div className="text-right">
                  <div className="text-[#838998] text-xs font-normal">&nbsp;</div>
                  <div className="text-white text-xs font-medium">Δ Valor</div>
                </div>
              ),
              cell: (info) => {
                const item = info.row.original;
                if (item.category === 'header') return null;

                // Handle client breakdown rows
                if (item.category === 'client_breakdown') {
                  if (item.isOutros || !item.codigoCliente) {
                    return <div className="text-right text-[#5A5D66] text-sm">-</div>;
                  }

                  const receitaBreakdowns = scenarioBreakdowns?.get('ReceitaBruta') ?? [];
                  const clientBreakdown = receitaBreakdowns.find(b => b.dimensionId === item.codigoCliente);
                  const clientProaValue = clientBreakdown?.annual ?? null;
                  const clientActual = item.staticValue ?? 0;

                  if (clientProaValue === null) {
                    return <div className="text-right text-[#5A5D66] text-sm">-</div>;
                  }

                  const diff = clientActual - clientProaValue;
                  const isGood = diff > 0;
                  const isBad = diff < 0;

                  return (
                    <div className={`
                      text-right text-xs
                      ${isGood ? 'text-green-400' : isBad ? 'text-red-400' : 'text-[#838998]'}
                    `}>
                      {diff > 0 ? '+' : ''}<SensitiveValue mask="R$ ••••••">{formatCurrency(diff)}</SensitiveValue>
                    </div>
                  );
                }

                const actual = rowTotals[item.id] ?? 0;
                const proaValue = item.drillDownLineItem && scenarioMetrics
                  ? scenarioMetrics.get(item.drillDownLineItem) ?? null
                  : null;

                if (proaValue === null) {
                  return <div className="text-right text-[#5A5D66]">-</div>;
                }

                // For expenses (shown negative in DRE), compare absolute values
                // PROA stores all values as positive
                const isExpense = isExpenseRow(item);
                const actualAbs = Math.abs(actual);
                const diff = isExpense
                  ? actualAbs - proaValue  // Compare absolute: actual expense vs budgeted expense
                  : actual - proaValue;     // Revenue: direct comparison

                // For expenses: positive diff means spent MORE than budget (bad)
                // For revenue: positive diff means earned MORE than budget (good)
                const isGood = isExpense ? diff < 0 : diff > 0;
                const isBad = isExpense ? diff > 0 : diff < 0;

                return (
                  <div className={`
                    text-right text-sm
                    ${isGood ? 'text-green-400' : isBad ? 'text-red-400' : 'text-[#838998]'}
                  `}>
                    {diff > 0 ? '+' : ''}<SensitiveValue mask="R$ ••••••">{formatCurrency(diff)}</SensitiveValue>
                  </div>
                );
              },
            })
          );

          // PROA Variação column
          cols.push(
            columnHelper.display({
              id: `proa_var_${scenario.scenarioId}`,
              header: () => (
                <div className="text-right">
                  <div className="text-[#838998] text-xs font-normal">&nbsp;</div>
                  <div className="text-white text-xs font-medium">Δ %</div>
                </div>
              ),
              cell: (info) => {
                const item = info.row.original;
                if (item.category === 'header') return null;

                // Handle client breakdown rows
                if (item.category === 'client_breakdown') {
                  if (item.isOutros || !item.codigoCliente) {
                    return <div className="text-right text-[#5A5D66] text-sm">-</div>;
                  }

                  const receitaBreakdowns = scenarioBreakdowns?.get('ReceitaBruta') ?? [];
                  const clientBreakdown = receitaBreakdowns.find(b => b.dimensionId === item.codigoCliente);
                  const clientProaValue = clientBreakdown?.annual ?? null;
                  const clientActual = item.staticValue ?? 0;

                  if (clientProaValue === null || clientProaValue === 0) {
                    return <div className="text-right text-[#5A5D66] text-sm">-</div>;
                  }

                  const variance = ((clientActual - clientProaValue) / clientProaValue) * 100;
                  const isGood = variance > 0;
                  const isBad = variance < 0;

                  return (
                    <div className={`
                      text-right text-xs font-medium
                      ${isGood ? 'text-green-400' : isBad ? 'text-red-400' : 'text-[#838998]'}
                    `}>
                      {variance > 0 ? '▲' : variance < 0 ? '▼' : ''} {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
                    </div>
                  );
                }

                const actual = rowTotals[item.id] ?? 0;
                const proaValue = item.drillDownLineItem && scenarioMetrics
                  ? scenarioMetrics.get(item.drillDownLineItem) ?? null
                  : null;

                if (proaValue === null || proaValue === 0) {
                  return <div className="text-right text-[#5A5D66]">-</div>;
                }

                // For expenses (shown negative in DRE), compare absolute values
                const isExpense = isExpenseRow(item);
                const actualAbs = Math.abs(actual);
                const variance = isExpense
                  ? ((actualAbs - proaValue) / proaValue) * 100
                  : ((actual - proaValue) / proaValue) * 100;

                // For expenses: positive variance means spent MORE than budget (bad)
                // For revenue: positive variance means earned MORE than budget (good)
                const isGood = isExpense ? variance < 0 : variance > 0;
                const isBad = isExpense ? variance > 0 : variance < 0;

                return (
                  <div className={`
                    text-right text-sm font-medium
                    ${isGood ? 'text-green-400' : isBad ? 'text-red-400' : 'text-[#838998]'}
                  `}>
                    {variance > 0 ? '▲' : variance < 0 ? '▼' : ''} {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
                  </div>
                );
              },
            })
          );
        });
      }
    }

    // MONTHLY VIEW
    else if (granularity === 'monthly') {
      sortedMonths.forEach((monthKey) => {
        const [year, month] = monthKey.split('-').map(Number);
        const monthName = MONTH_NAMES[month - 1];
        const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const monthEnd = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

        cols.push(
          columnHelper.display({
            id: monthKey,
            header: () => (
              <div className="text-right">
                <div className="font-bold text-white">{monthName}</div>
                <div className="text-xs text-[#838998]">{year}</div>
              </div>
            ),
            cell: (info) => {
              const item = info.row.original;
              const row = monthlyData[monthKey];
              const rawValue = row ? item.getValue(row) : null;
              const value = rawValue !== null ? toNum(rawValue) : null;
              // Pass monthKey as period for client breakdown rows
              return createValueCell(item, value, monthStart, monthEnd, `Ver ${item.label} em ${monthName}/${year}`, monthKey);
            },
          })
        );
      });

      // Total column
      cols.push(
        columnHelper.display({
          id: 'total',
          header: () => <div className="text-right font-bold text-white">TOTAL</div>,
          cell: (info) => {
            const item = info.row.original;
            if (item.category === 'header') return null;
            // Client breakdown rows show staticValue for total
            if (item.category === 'client_breakdown') {
              const clientTotal = item.staticValue ?? 0;
              // Make individual clients clickable (not OUTROS) - requires codigoCliente for navigation
              if (!item.isOutros && item.codigoCliente) {
                return (
                  <div
                    onClick={() => handleClientDrillDown(item.codigoCliente!)}
                    className="text-right text-[#838998] text-sm no-underline cursor-pointer hover:underline hover:text-white"
                    title={`Ver detalhes de ${item.clientName}`}
                  >
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(clientTotal)}</SensitiveValue>
                  </div>
                );
              }
              return (
                <div className="text-right text-[#838998] text-sm">
                  <SensitiveValue mask="R$ ••••••">{formatCurrency(clientTotal)}</SensitiveValue>
                </div>
              );
            }
            const total = rowTotals[item.id] ?? 0;
            return (
              <div className={`
                text-right ${item.isBold ? 'font-bold' : 'font-medium'}
                ${total === 0 ? 'text-brand-gray-dark' : item.id === 'lucro_liquido' ? (total < 0 ? 'text-red-400' : 'text-[#D2FA52]') : item.category === 'tax' || item.category === 'cost' || item.category === 'expense' || total < 0 ? 'text-red-400' : 'text-white'}
              `}>
                {item.id === 'lucro_liquido' ? (
                  <span className="inline-block bg-brand-dark-deepest rounded px-2 py-1 text-[15px]">
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                  </span>
                ) : (
                  <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                )}
              </div>
            );
          },
        })
      );

      // Note: PROA columns are only shown in Accumulated view
    }

    // QUARTERLY VIEW
    else if (granularity === 'quarterly') {
      sortedQuarters.forEach((quarterKey) => {
        const [year, q] = quarterKey.split('-');
        const quarterNum = parseInt(q.replace('Q', ''));
        const quarterLabel = QUARTER_NAMES[quarterNum - 1];
        const quarterStartMonth = (quarterNum - 1) * 3 + 1;
        const quarterEndMonth = quarterNum * 3;
        const quarterStart = `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(parseInt(year), quarterEndMonth, 0).getDate();
        const quarterEnd = `${year}-${String(quarterEndMonth).padStart(2, '0')}-${lastDay}`;

        // Build list of months in this quarter for client breakdown aggregation
        const quarterMonths = [
          `${year}-${String(quarterStartMonth).padStart(2, '0')}`,
          `${year}-${String(quarterStartMonth + 1).padStart(2, '0')}`,
          `${year}-${String(quarterEndMonth).padStart(2, '0')}`,
        ];

        cols.push(
          columnHelper.display({
            id: quarterKey,
            header: () => (
              <div className="text-right">
                <div className="font-bold text-white">{quarterLabel}</div>
                <div className="text-xs text-[#838998]">{year}</div>
              </div>
            ),
            cell: (info) => {
              const item = info.row.original;

              // For client breakdown rows, sum monthly values for this quarter
              if (item.category === 'client_breakdown' && item.monthlyValues) {
                let quarterTotal = 0;
                for (const m of quarterMonths) {
                  quarterTotal += item.monthlyValues[m] ?? 0;
                }
                // Make individual clients clickable (not OUTROS) - requires codigoCliente for navigation
                if (!item.isOutros && item.codigoCliente) {
                  return (
                    <div
                      onClick={() => handleClientDrillDown(item.codigoCliente!)}
                      className="text-right text-white text-sm no-underline cursor-pointer hover:underline hover:text-brand-gray-light"
                      title={`Ver detalhes de ${item.clientName}`}
                    >
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(quarterTotal)}</SensitiveValue>
                    </div>
                  );
                }
                return (
                  <div className="text-right text-[#838998] text-sm">
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(quarterTotal)}</SensitiveValue>
                  </div>
                );
              }

              const quarterRows = quarterlyData[quarterKey] || [];
              const value = sumField(quarterRows, item.getValue);
              return createValueCell(item, value, quarterStart, quarterEnd, `Ver ${item.label} em ${quarterLabel}/${year}`);
            },
          })
        );
      });

      // Total column
      cols.push(
        columnHelper.display({
          id: 'total',
          header: () => <div className="text-right font-bold text-white">TOTAL</div>,
          cell: (info) => {
            const item = info.row.original;
            if (item.category === 'header') return null;
            // Client breakdown rows show staticValue for total
            if (item.category === 'client_breakdown') {
              const clientTotal = item.staticValue ?? 0;
              // Make individual clients clickable (not OUTROS) - requires codigoCliente for navigation
              if (!item.isOutros && item.codigoCliente) {
                return (
                  <div
                    onClick={() => handleClientDrillDown(item.codigoCliente!)}
                    className="text-right text-[#838998] text-sm no-underline cursor-pointer hover:underline hover:text-white"
                    title={`Ver detalhes de ${item.clientName}`}
                  >
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(clientTotal)}</SensitiveValue>
                  </div>
                );
              }
              return (
                <div className="text-right text-[#838998] text-sm">
                  <SensitiveValue mask="R$ ••••••">{formatCurrency(clientTotal)}</SensitiveValue>
                </div>
              );
            }
            const total = rowTotals[item.id] ?? 0;
            return (
              <div className={`
                text-right ${item.isBold ? 'font-bold' : 'font-medium'}
                ${total === 0 ? 'text-brand-gray-dark' : item.id === 'lucro_liquido' ? (total < 0 ? 'text-red-400' : 'text-[#D2FA52]') : item.category === 'tax' || item.category === 'cost' || item.category === 'expense' || total < 0 ? 'text-red-400' : 'text-white'}
              `}>
                {item.id === 'lucro_liquido' ? (
                  <span className="inline-block bg-brand-dark-deepest rounded px-2 py-1 text-[15px]">
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                  </span>
                ) : (
                  <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                )}
              </div>
            );
          },
        })
      );

      // Note: PROA columns are only shown in Accumulated view
    }

    // YEARLY VIEW
    else if (granularity === 'yearly') {
      sortedYears.forEach((year) => {
        const yearStart = `${year}-01-01`;
        const yearEnd = `${year}-12-31`;

        cols.push(
          columnHelper.display({
            id: year,
            header: () => <div className="text-right font-bold text-white">{year}</div>,
            cell: (info) => {
              const item = info.row.original;

              // For client breakdown rows, sum all monthly values for this year
              if (item.category === 'client_breakdown' && item.monthlyValues) {
                let yearTotal = 0;
                for (const [period, value] of Object.entries(item.monthlyValues)) {
                  if (period.startsWith(year)) {
                    yearTotal += value;
                  }
                }
                // Make individual clients clickable (not OUTROS) - requires codigoCliente for navigation
                if (!item.isOutros && item.codigoCliente) {
                  return (
                    <div
                      onClick={() => handleClientDrillDown(item.codigoCliente!)}
                      className="text-right text-white text-sm no-underline cursor-pointer hover:underline hover:text-brand-gray-light"
                      title={`Ver detalhes de ${item.clientName}`}
                    >
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(yearTotal)}</SensitiveValue>
                    </div>
                  );
                }
                return (
                  <div className="text-right text-[#838998] text-sm">
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(yearTotal)}</SensitiveValue>
                  </div>
                );
              }

              const yearRows = yearlyData[year] || [];
              const value = sumField(yearRows, item.getValue);
              return createValueCell(item, value, yearStart, yearEnd, `Ver ${item.label} em ${year}`);
            },
          })
        );
      });

      if (sortedYears.length > 1) {
        cols.push(
          columnHelper.display({
            id: 'total',
            header: () => <div className="text-right font-bold text-white">TOTAL</div>,
            cell: (info) => {
              const item = info.row.original;
              if (item.category === 'header') return null;
              // Client breakdown rows show staticValue for total
              if (item.category === 'client_breakdown') {
                const clientTotal = item.staticValue ?? 0;
                // Make individual clients clickable (not OUTROS) - requires codigoCliente for navigation
                if (!item.isOutros && item.codigoCliente) {
                  return (
                    <div
                      onClick={() => handleClientDrillDown(item.codigoCliente!)}
                      className="text-right text-[#838998] text-sm no-underline cursor-pointer hover:underline hover:text-white"
                      title={`Ver detalhes de ${item.clientName}`}
                    >
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(clientTotal)}</SensitiveValue>
                    </div>
                  );
                }
                return (
                  <div className="text-right text-[#838998] text-sm">
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(clientTotal)}</SensitiveValue>
                  </div>
                );
              }
              const total = rowTotals[item.id] ?? 0;
              return (
                <div className={`
                  text-right ${item.isBold ? 'font-bold' : 'font-medium'}
                  ${total === 0 ? 'text-brand-gray-dark' : item.id === 'lucro_liquido' ? (total < 0 ? 'text-red-400' : 'text-[#D2FA52]') : item.category === 'tax' || item.category === 'cost' || item.category === 'expense' || total < 0 ? 'text-red-400' : 'text-white'}
                `}>
                  {item.id === 'lucro_liquido' ? (
                    <span className="inline-block bg-brand-dark-deepest rounded px-2 py-1 text-[15px]">
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                    </span>
                  ) : (
                    <SensitiveValue mask="R$ ••••••">{formatCurrency(total)}</SensitiveValue>
                  )}
                </div>
              );
            },
          })
        );
      }

      // Note: PROA columns are only shown in Accumulated view
    }

    return cols;
  }, [
    columnHelper, granularity, sortedMonths, sortedQuarters, sortedYears,
    monthlyData, quarterlyData, yearlyData, rowTotals, comparisonTotals,
    comparisonRange, startDate, endDate, receitaBrutaTotal, handleDrillDown,
    handleClientDrillDown, toggleSection, collapsedSections, calcDelta,
    proaComparison.enabled, selectedProaScenarios, proaValuesByScenario, proaBreakdownsByScenario
  ]);

  const table = useReactTable({
    data: visibleRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Admin-only access control
  if (!user || (user.role !== 'admin' && user.role !== 'diretor-executivo' && user.role !== 'diretor-financeiro' && user.role !== 'diretor-tecnico' && user.role !== 'vice-presidente' && user.role !== 'ceo')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
        <Receipt className="h-16 w-16 text-brand-red" />
        <p className="text-3xl font-bold text-white font-display">Acesso Restrito</p>
        <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
          Esta página contém informações financeiras sensíveis (DRE) e está disponível apenas para administradores.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls Panel */}
      <DREControlsPanel
        startDate={startDate}
        endDate={endDate}
        onPeriodChange={(dates) => {
          setStartDate(dates.startDate);
          setEndDate(dates.endDate);
        }}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onRefresh={() => void refetch()}
        isLoading={isLoading}
        comparisonMode={comparisonMode}
        onComparisonModeChange={setComparisonMode}
        comparisonRange={comparisonRange}
        granularity={granularity}
        onGranularityChange={setGranularity}
        proaComparison={proaComparison}
        onProaComparisonChange={handleProaComparisonChange}
        presets={presets}
        onSavePreset={openPresetModal}
        onApplyPreset={handleApplyPreset}
        onDeletePreset={deleteFilterPreset}
      />

      {/* Save Preset Modal */}
      <SavePresetModal
        isOpen={isPresetModalOpen}
        onClose={closePresetModal}
        onSave={handleSavePreset}
        currentFilters={{
          startDate,
          endDate,
          granularity,
          comparisonMode,
        }}
        checkNameExists={checkNameExists}
      />

      {/* Error Display */}
      {error && (
        <FinancialEmptyState
          variant="error"
          description={error.message}
          actionLabel="Tentar novamente"
          onAction={() => refetch()}
        />
      )}

      {/* Loading State */}
      {isLoading ? (
        <FinancialSkeleton variant="full-dashboard" />
      ) : dreData.length > 0 ? (
        <>
          {/* Insights Panel */}
          {dreInsights.length > 0 && (
            <InsightsPanel
              insights={dreInsights}
              defaultExpanded={false}
              title="Insights do Período"
            />
          )}

          {/* KPI Cards - Responsive Grid
              mobile (<640px): 2 columns
              sm (≥640px): 3 columns (tablet)
              lg (≥1024px): 4 columns (desktop)
              xl (≥1280px): 6 columns (large desktop)
          */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
            {KPI_DEFINITIONS.map((kpi) => {
              const value = kpi.getValue(waterfallValues);
              const compValue = comparisonRange ? kpi.getValue(comparisonWaterfallValues) : 0;
              const change = calcDelta(value, compValue);

              let subtitle: string | undefined;
              const faturamentoTotal = waterfallValues['faturamento'] || 0;
              if (kpi.key === 'faturamento') {
                subtitle = undefined;
              } else if (kpi.key === 'receitaBruta') {
                subtitle = faturamentoTotal ? `${((value / faturamentoTotal) * 100).toFixed(1)}% do Faturamento` : undefined;
              } else if (kpi.getSubtitle) {
                subtitle = kpi.getSubtitle(value, receitaBrutaTotal);
              } else if (receitaBrutaTotal) {
                subtitle = `${((value / receitaBrutaTotal) * 100).toFixed(1)}% da Receita`;
              }

              return (
                <KPICard
                  key={kpi.key}
                  label={kpi.label}
                  value={formatCompactCurrency(value)}
                  change={change}
                  subtitle={subtitle}
                  icon={kpi.icon}
                  active={selectedMetrics.has(kpi.key)}
                  onClick={() => toggleMetric(kpi.key)}
                  sparklineData={sparklineDataByKPI[kpi.key]}
                  sparklineLabels={sparklineLabels}
                  numericValue={value}
                  animationFormat="compact-currency"
                  valueColor={kpi.valueColor}
                  sparklineColor={kpi.sparklineColor}
                  invertDelta={kpi.invertDelta}
                  comparisonLabel={comparisonMode === 'same_period_last_year' ? 'vs mesmo período ano anterior' : comparisonMode === 'previous_period' ? 'vs período anterior' : undefined}
                />
              );
            })}
          </div>

          {/* Chart Area */}
          <div>
            {/* Tab Navigation */}
            <div className="flex items-center gap-6 border-b border-brand-dark-lighter overflow-x-auto overflow-y-hidden mb-4">
              {[
                { id: 'evolution' as const, label: 'Evolução Mensal' },
                { id: 'waterfall' as const, label: 'Composição' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveChart(tab.id)}
                  className={`pb-2 -mb-px text-sm font-medium transition-colors border-b-2 whitespace-nowrap cursor-pointer ${
                    activeChart === tab.id
                      ? 'text-white border-brand-primary'
                      : 'text-brand-gray border-transparent hover:text-brand-gray-light'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Chart Content */}
            <div className="p-4">
              {activeChart === 'evolution' && (
                <DREChart
                  data={chartData}
                  comparisonData={comparisonChartData}
                  selectedMetrics={selectedMetrics}
                  onToggleMetric={toggleMetric}
                  chartType="combo"
                  height={340}
                />
              )}

              {activeChart === 'waterfall' && (
                <WaterfallChart
                  values={waterfallValues}
                  selectedMetrics={selectedMetrics}
                  height={300}
                />
              )}
            </div>
          </div>

          {/* DRE Table */}
          <div className="bg-brand-dark-light rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full w-auto text-sm font-body">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-brand-dark-lighter">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`
                            p-3 whitespace-nowrap border-b border-brand-primary/30 text-brand-gray-light font-semibold text-xs uppercase tracking-wider
                            ${header.id === 'label' ? 'text-left sticky left-0 bg-brand-dark-lighter z-10' : 'text-right'}
                            ${header.id === 'total' || header.id === 'accumulated_total' ? 'border-l border-l-white/10' : ''}
                          `}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => {
                    const item = row.original;
                    const isHeaderRow = item.category === 'header';
                    const isTotalRow = item.isTotal;
                    const isClientBreakdown = item.category === 'client_breakdown';

                    return (
                      <tr
                        key={row.id}
                        className={`
                          ${isHeaderRow ? 'bg-brand-primary/8' : ''}
                          ${isTotalRow && item.id === 'lucro_liquido' ? 'bg-[#D2FA52]' : ''}
                          ${isTotalRow && item.id !== 'lucro_liquido' ? 'bg-[#2d3138] border-t border-t-white/10 border-l-2 border-l-[#D2FA52]/50' : ''}
                          ${isClientBreakdown ? 'bg-brand-dark-deepest hover:bg-brand-dark' : ''}
                          ${!isHeaderRow && !isTotalRow && !isClientBreakdown ? 'bg-brand-dark hover:bg-brand-dark-lighter' : ''}
                          transition-colors duration-100
                        `}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`
                              ${isTotalRow ? 'py-3 px-2' : 'p-2'} whitespace-nowrap
                              ${isTotalRow ? 'border-b border-brand-dark-lighter' : 'border-b border-brand-dark-lighter/60'}
                              ${cell.column.id === 'label' ? 'sticky left-0 z-10' : ''}
                              ${cell.column.id === 'label' && isHeaderRow ? 'bg-brand-primary/8' : ''}
                              ${cell.column.id === 'label' && isTotalRow && item.id === 'lucro_liquido' ? 'bg-[#D2FA52]' : ''}
                              ${cell.column.id === 'label' && isTotalRow && item.id !== 'lucro_liquido' ? 'bg-[#2d3138] border-l-2 border-l-[#D2FA52]/50' : ''}
                              ${cell.column.id === 'label' && isClientBreakdown ? 'bg-brand-dark-deepest' : ''}
                              ${cell.column.id === 'label' && !isHeaderRow && !isTotalRow && !isClientBreakdown ? 'bg-brand-dark' : ''}
                              ${cell.column.id === 'total' || cell.column.id === 'accumulated_total' ? 'border-l border-l-white/10' : ''}
                            `}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="h-16" />
        </>
      ) : (
        <FinancialEmptyState
          variant="no-data"
          description="Não há dados de DRE para o período selecionado"
        />
      )}
    </div>
  );
}

export default DREDashboard;
